import { z } from 'zod';
/**
 * Buzón de correo entrante de la entidad: el que la plataforma lee para radicar las solicitudes que llegan por correo
 * (pqrs@entidad.gov.co). Los secretos (secreto del cliente, llave de la cuenta de servicio, contraseña IMAP) se
 * guardan cifrados y nunca vuelven a la web. Sin el campo del secreto se conserva el guardado (si el destino no
 * cambió); `null` lo quita. Con `credentials: 'outbound'` se usa la misma aplicación o cuenta de servicio del correo
 * saliente (solo Microsoft 365 y Google Workspace, cuando el proveedor coincide) y no hay secreto propio.
 */
export const MailboxProvider = {
    MICROSOFT_365: 'MICROSOFT_365',
    GOOGLE_WORKSPACE: 'GOOGLE_WORKSPACE',
    IMAP: 'IMAP',
};
export const MAILBOX_PROVIDER_LABEL = {
    MICROSOFT_365: 'Microsoft 365',
    GOOGLE_WORKSPACE: 'Google Workspace',
    IMAP: 'Servidor IMAP',
};
/** `own`: credenciales propias; `outbound`: las del correo saliente (misma aplicación o cuenta de servicio). */
export const MAILBOX_CREDENTIALS = ['own', 'outbound'];
/** `none` solo para pruebas en red local (GreenMail). */
export const IMAP_SECURITY = ['tls', 'starttls', 'none'];
/**
 * Servicios conocidos: llenan servidor, puerto y seguridad. «custom» deja todo a mano. Una cuenta normal de Gmail
 * sirve con una contraseña de aplicación; Google Workspace y Microsoft 365 van por su opción propia (ya no aceptan
 * la contraseña de la cuenta por IMAP), y Outlook.com personal tampoco.
 */
export const IMAP_PRESETS = {
    custom: { label: 'Otro servidor', host: '', port: 993, security: 'tls', hint: 'Los datos los da quien administra el correo de la entidad. Lo habitual es el puerto 993 con TLS.' },
    gmail: { label: 'Gmail (contraseña de aplicación)', host: 'imap.gmail.com', port: 993, security: 'tls', hint: 'Usuario: la cuenta de Gmail. Contraseña: una contraseña de aplicación de 16 letras, que se crea en myaccount.google.com/apppasswords con la cuenta abierta (exige verificación en dos pasos; Google la desaconseja, pero es lo que ofrece sin «Iniciar sesión con Google»). Para Google Workspace use la opción Google Workspace.' },
    zoho: { label: 'Zoho Mail', host: 'imap.zoho.com', port: 993, security: 'tls', hint: 'Usuario: la cuenta de correo. Contraseña: una contraseña específica de aplicación, con el acceso IMAP habilitado en Zoho. Cuentas en Europa: imap.zoho.eu.' },
    yahoo: { label: 'Yahoo Mail', host: 'imap.mail.yahoo.com', port: 993, security: 'tls', hint: 'Usuario: la cuenta de correo. Contraseña: una contraseña de aplicación generada en la seguridad de la cuenta.' },
};
const IMAP_PRESET_KEYS = Object.keys(IMAP_PRESETS);
/** Cada cuánto se revisa el buzón (segundos). */
export const MAILBOX_POLL_INTERVALS = [60, 120, 300, 900];
export const MAILBOX_POLL_MIN = 30;
export const MAILBOX_POLL_MAX = 900;
const email = z.string().trim().toLowerCase().email('Correo no válido').max(320);
const optionalText = (max) => z.string().trim().max(max).optional().nullable().transform((v) => v || null);
const secret = (max) => z.string().max(max).optional().nullable();
/** Lo común a todo proveedor. */
const common = {
    /** Dirección del buzón. Es la «dirección de recepción» de la entidad (settings `email.inbound.address`). */
    mailbox: email,
    /** Carpeta (o etiqueta) a la que se mueve lo ya radicado. Vacío: solo se marca como leído. Nunca se borra nada. */
    processedFolder: z.string().trim().max(100).optional().nullable().transform((v) => v || null),
    pollIntervalSeconds: z.coerce.number().int().min(MAILBOX_POLL_MIN).max(MAILBOX_POLL_MAX).default(60),
};
export const microsoft365MailboxSchema = z.object({
    provider: z.literal('MICROSOFT_365'),
    credentials: z.enum(MAILBOX_CREDENTIALS).default('own'),
    /** Id del directorio (inquilino) de Microsoft Entra. */
    tenantId: optionalText(100),
    /** Id de la aplicación registrada. */
    clientId: z.string().trim().optional().nullable().transform((v) => v || null).refine((v) => !v || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v), 'El id de la aplicación es un GUID'),
    clientSecret: secret(500),
    ...common,
});
export const googleWorkspaceMailboxSchema = z.object({
    provider: z.literal('GOOGLE_WORKSPACE'),
    credentials: z.enum(MAILBOX_CREDENTIALS).default('own'),
    serviceAccountEmail: z.string().trim().toLowerCase().max(320).optional().nullable().transform((v) => v || null).refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'Correo no válido'),
    /** Llave privada (PEM) de la cuenta de servicio. */
    privateKey: secret(10_000).refine((k) => !k || /-----BEGIN (RSA )?PRIVATE KEY-----/.test(k), 'La llave debe ser la «private_key» del JSON de la cuenta de servicio'),
    ...common,
});
export const imapMailboxSchema = z.object({
    provider: z.literal('IMAP'),
    preset: z.enum(IMAP_PRESET_KEYS).default('custom'),
    host: z.string().trim().min(1, 'Indica el servidor').max(255).regex(/^[a-zA-Z0-9.-]+$/, 'Servidor no válido'),
    port: z.coerce.number().int().min(1).max(65535).default(993),
    security: z.enum(IMAP_SECURITY).default('tls'),
    user: z.string().trim().min(1, 'Indica el usuario').max(320),
    password: secret(500),
    /** Carpeta que se lee. */
    folder: z.string().trim().min(1).max(200).default('INBOX'),
    ...common,
});
/** Zod 3 no admite `superRefine` en los miembros de una unión discriminada: va sobre la unión. */
export const inboundMailboxSchema = z.discriminatedUnion('provider', [microsoft365MailboxSchema, googleWorkspaceMailboxSchema, imapMailboxSchema]).superRefine((v, ctx) => {
    if (v.provider === 'MICROSOFT_365' && v.credentials === 'own') {
        if (!v.tenantId)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tenantId'], message: 'Indica el id del directorio' });
        if (!v.clientId)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientId'], message: 'Indica el id de la aplicación' });
    }
    if (v.provider === 'GOOGLE_WORKSPACE' && v.credentials === 'own' && !v.serviceAccountEmail) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serviceAccountEmail'], message: 'Indica la cuenta de servicio' });
    }
});
/** El campo secreto de cada proveedor. */
export const MAILBOX_SECRET_FIELD = { MICROSOFT_365: 'clientSecret', GOOGLE_WORKSPACE: 'privateKey', IMAP: 'password' };
/** ¿Este buzón usa las credenciales del correo saliente? */
export function mailboxUsesOutbound(c) {
    return c.provider !== 'IMAP' && c.credentials === 'outbound';
}
/** Proveedor del correo saliente que puede prestar sus credenciales a este buzón (mismo servicio). */
export function mailboxOutboundProvider(provider) {
    return provider === 'IMAP' ? null : provider;
}
/**
 * A dónde se le entrega el secreto. El guardado solo se reutiliza si esto no cambió, para que nadie lo envíe a otro
 * servidor cambiando solo la dirección. Con credenciales del saliente no hay secreto propio.
 */
export function inboundMailboxTarget(c) {
    if (mailboxUsesOutbound(c))
        return `${c.provider}:outbound`;
    switch (c.provider) {
        case 'MICROSOFT_365': return `m365:${(c.tenantId ?? '').toLowerCase()}:${(c.clientId ?? '').toLowerCase()}`;
        case 'GOOGLE_WORKSPACE': return `google:${(c.serviceAccountEmail ?? '').toLowerCase()}`;
        case 'IMAP': return `imap:${c.host.toLowerCase()}:${c.user.toLowerCase()}`;
    }
}
/** Probar: con el formulario o, sin él, con lo guardado. Solo se conecta; no lee nada. */
export const testInboundMailboxSchema = z.object({ config: inboundMailboxSchema.optional() });
/** Por qué un correo del buzón no se radicó. */
export const IGNORED_EMAIL_REASON_LABEL = {
    'no-from': 'Sin remitente',
    'auto-submitted': 'Respuesta automática',
    'auto-reply': 'Respuesta automática',
    precedence: 'Correo masivo',
    list: 'Lista de correo',
    bounce: 'Rebote del servidor',
    noreply: 'Remitente que no recibe respuestas',
    'own-address': 'Correo de la propia entidad',
    'auto-create-off': 'Creación automática apagada',
    'not-request': 'El motor de decisión no lo reconoció como solicitud',
};
//# sourceMappingURL=email-inbound.js.map